import type { FilterCheckbox, FilterObject } from 'core-system/Table/FilterModel'
import { filterRows } from 'core-system/Table/helperFunctions'
import palette from 'core-system/Themes/palette'
import type { ChipVariants } from 'core-system/Chip/Chip'
import { uniq } from 'lodash-es'
import moment from 'moment'
import type { MyCommuterTransaction } from 'redux/employee/employeeTypes'
import FormattingUtils from 'shared/FormattingUtils'

export const transactionStatusLabel = (status?: string | null) => {
  if (status === 'capture') {
    return 'Approved'
  }
  if (status === 'refund') {
    return 'Refund'
  }
  if (status === 'reimbursement') {
    return 'Reimbursement'
  }
  if (status === 'declined') {
    return 'Declined'
  }
  if (!status) {
    return '—'
  }
  return status.charAt(0).toUpperCase() + status.slice(1)
}

export const transactionStatusVariant = (
  status?: string | null
): ChipVariants => {
  if (status === 'capture') {
    return 'green'
  }
  if (status === 'refund') {
    return 'grey'
  }
  if (status === 'reimbursement') {
    return 'blue'
  }
  if (status === 'declined') {
    return 'red'
  }
  return 'grey'
}

const BUCKET_COLORS: Record<string, string> = {
  COMMUTER_TRANSIT: palette.secondary.purple500,
  COMMUTER_PARKING: palette.accent.blue500,
  GRH: palette.warning.yellow700,
  FLEX: palette.success.green500,
  TRANSIT_PASS: palette.secondary.purple500,
  OTP: palette.accent.teal500,
  MICROMOBILITY: palette.accent.teal500,
  WALLET: palette.grey.grey600,
  REWARDS: palette.primary.pink500,
}

export const transactionBucketColor = (kind?: string) =>
  (kind && BUCKET_COLORS[kind]) || palette.grey.grey500

const STATUS_FILTER_ORDER = ['Approved', 'Refund', 'Reimbursement', 'Declined']

const BUCKET_FILTER_ORDER = [
  'Transit',
  'Parking',
  'Flex',
  'GRH',
  'Transit Pass',
  'OTP',
  'Micromobility',
  'Wallet',
  'Rewards',
]

export interface TransactionTableRow {
  id: string
  date: string
  merchant: string
  status: string
  buckets: string[]
  bucket: string
  amount: string
  created: string
  transaction: MyCommuterTransaction
}

export const TRANSACTION_SEARCH_KEYS = [
  'date',
  'merchant',
  'bucket',
  'amount',
  'status',
]

const transactionAmountLabel = (transaction: MyCommuterTransaction) => {
  const buckets = transaction.buckets || []
  if (!buckets.length) {
    return FormattingUtils.formatDollar(transaction.spend)
  }
  return buckets
    .map((bucket) => FormattingUtils.formatDollar(bucket.amount))
    .join(' ')
}

export const buildTransactionRows = (
  transactions: MyCommuterTransaction[]
): TransactionTableRow[] =>
  transactions.map((transaction) => {
    const buckets = uniq(
      (transaction.buckets || []).map((bucket) => bucket.name)
    )
    const created = transaction.created
      ? moment(transaction.created)
      : null
    return {
      id: transaction.internalId,
      date: created?.isValid() ? created.format('MMM D') : '—',
      merchant: transaction.name || '—',
      status: transactionStatusLabel(transaction.status),
      buckets,
      bucket: buckets.join(' ') || '—',
      amount: transactionAmountLabel(transaction),
      created: transaction.created || '',
      transaction,
    }
  })

const checkboxGroup = (header: string, key: string, labels: string[]) => ({
  header,
  checkboxes: labels.map((value) => ({
    label: value,
    checked: false,
    key,
    value,
  })),
})

export const transactionFilters = (
  rows: TransactionTableRow[]
): FilterObject[] => {
  const statusLabels = rows.map((row) => row.status)
  const knownStatuses = STATUS_FILTER_ORDER.filter((status) =>
    statusLabels.includes(status)
  )
  const extraStatuses = uniq(statusLabels).filter(
    (status) => !STATUS_FILTER_ORDER.includes(status)
  )
  const bucketLabels = rows.flatMap((row) => row.buckets)
  const knownBuckets = BUCKET_FILTER_ORDER.filter((name) =>
    bucketLabels.includes(name)
  )
  const extraBuckets = uniq(bucketLabels).filter(
    (name) => !BUCKET_FILTER_ORDER.includes(name)
  )
  const filters = [
    checkboxGroup('Status', 'status', [...knownStatuses, ...extraStatuses]),
  ]
  if (knownBuckets.length || extraBuckets.length) {
    filters.push(
      checkboxGroup('Bucket', 'buckets', [...knownBuckets, ...extraBuckets])
    )
  }
  return filters
}

export const applyTransactionFilters = (
  rows: TransactionTableRow[],
  filters: FilterCheckbox[]
) => {
  const statusFilters = filters.filter((filter) => filter.key !== 'buckets')
  const bucketFilters = filters
    .filter((filter) => filter.key === 'buckets')
    .map((filter) => String(filter.value))
  let next = rows
  if (statusFilters.length) {
    next = filterRows(next, statusFilters) as TransactionTableRow[]
  }
  if (bucketFilters.length) {
    next = filterTransactionsByBucket(next, bucketFilters)
  }
  return next
}

export const filterTransactionsByBucket = (
  rows: TransactionTableRow[],
  selectedBuckets: string[]
) => {
  if (!selectedBuckets.length) {
    return rows
  }
  return rows.filter((row) =>
    selectedBuckets.some((bucket) => row.buckets.includes(bucket))
  )
}
